import {
  DataState,
  DISABLE_GLOBAL_ERROR_TOASTS,
  FfdComponentStore,
  filterWithRemaining,
  Loadable,
  sortByDate,
  toDateFormat,
} from '@libs/utils';
import { inject, Injectable } from '@angular/core';
import { ImagesDataService, MessageAttachment, MessageContentAttachmentMediaTypeEnum } from '@ffd/api-client';
import { catchError, EMPTY, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { tapResponse, concatLatestFrom } from '@ngrx/operators';
import { fullSeriesTypes, ImagingGalleryItem, mapImagesToFullSeries } from '@libs/imaging';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@libs/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { ImagingGalleryAttachment } from '../message-input';
import {
  AddedGalleryItem,
  CollaborationCategorizedPatientImages,
  GalleryItemTabs,
  ImagingCategoryEnumToGalleryItemTabs,
  isGalleryItemTab,
} from './collaboration-image-selector-dialog.interface';

interface CollaborationImageSelectorDialogState {
  patientId: string;
  siteId: string;
  activeTab: GalleryItemTabs;
  galleryItems: Loadable<CollaborationCategorizedPatientImages>;
  selectedImages: ImagingGalleryAttachment[];
  addedGalleryItem: AddedGalleryItem[];
  attachments: Loadable<MessageAttachment[]>;
}

export interface CollaborationImageSelectorDialogViewModel {
  activeTab: GalleryItemTabs;
  galleryItems?: CollaborationCategorizedPatientImages;
  selectedImages: ImagingGalleryAttachment[];
  attachments: Loadable<MessageAttachment[]>;
}

@Injectable()
export class CollaborationImageSelectorDialogService extends FfdComponentStore<
  CollaborationImageSelectorDialogState,
  CollaborationImageSelectorDialogViewModel
> {
  constructor() {
    super({
      siteId: '',
      patientId: '',
      activeTab: GalleryItemTabs.FullSeries,
      selectedImages: [],
      galleryItems: {
        data: undefined,
        state: DataState.Initial,
      },
      addedGalleryItem: [],
      attachments: {
        data: [],
        state: DataState.Initial,
      },
    });
  }

  private readonly dataService = inject(ImagesDataService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  public readonly areAttachmentsLoadingSignal = this.selectSignal((state) => state.attachments.state === DataState.Loading);
  public readonly areImagesLoadingSignal = this.selectSignal(
    (state) => state.galleryItems.state === DataState.Loading || state.galleryItems.state === DataState.Initial,
  );

  public override init(): void {
    this.getMediaByTab(GalleryItemTabs.FullSeries);
    this.setActiveTab(GalleryItemTabs.FullSeries);
  }

  public setActiveTab = this.updater((state, activeTab: GalleryItemTabs): CollaborationImageSelectorDialogState => {
    return {
      ...state,
      activeTab,
    };
  });

  public setMappedMessagesAreLoading = this.updater((state): CollaborationImageSelectorDialogState => {
    return {
      ...state,
      attachments: {
        data: [],
        state: DataState.Loading,
      },
    };
  });

  public setMappedMessagesLoaded = this.updater((state): CollaborationImageSelectorDialogState => {
    return {
      ...state,
      attachments: {
        data: state.attachments.data,
        state: DataState.Fetched,
      },
    };
  });

  public setMappedMessagesAreErrored = this.updater((state): CollaborationImageSelectorDialogState => {
    return {
      ...state,
      attachments: {
        data: [],
        state: DataState.Error,
      },
    };
  });

  public setPatientAndSiteId = this.updater(
    (state, { patientId, siteId }: { patientId: string; siteId: string }): CollaborationImageSelectorDialogState => {
      return {
        ...state,
        patientId,
        siteId,
      };
    },
  );

  public setAttachments = this.updater((state, attachments: ImagingGalleryItem[]): CollaborationImageSelectorDialogState => {
    return {
      ...state,
      selectedImages: attachments,
    };
  });

  public addAttachment = this.updater((state, attachments: ImagingGalleryItem[]): CollaborationImageSelectorDialogState => {
    return {
      ...state,
      selectedImages: [...state.selectedImages, ...attachments],
    };
  });

  public removeAttachment = this.updater((state, attachments: ImagingGalleryItem[]): CollaborationImageSelectorDialogState => {
    return {
      ...state,
      selectedImages: state.selectedImages.filter((selectedImage) => !attachments.some((attachment) => attachment.id === selectedImage.id)),
    };
  });

  public getMediaByTab = this.effect(($: Observable<GalleryItemTabs>) =>
    $.pipe(
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      switchMap(([_, siteId, patientId]) => {
        return this.dataService
          .getImages(siteId, patientId, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              (patientImages) => {
                const galleryItems: ImagingGalleryItem[] = patientImages.images
                  .map((image) => ({
                    ...image,
                    category: image.type,
                    date: image.date ? toDateFormat(new Date(image.date)) : undefined,
                    mediaType: MessageContentAttachmentMediaTypeEnum.IMAGE,
                    name: image.toothPositionString,
                    groupLength: 0,
                  }))
                  .sort((a, b) => sortByDate({ date: a.date }, { date: b.date }, true));

                const [fullSeriesImages, others] = filterWithRemaining(galleryItems, (image) => {
                  return fullSeriesTypes.includes(image.category);
                });
                const fullSeries = mapImagesToFullSeries(fullSeriesImages);

                const tabImages = others.reduce(
                  (acc, image) => {
                    if (isGalleryItemTab(image.category)) {
                      const galleryItemTab = ImagingCategoryEnumToGalleryItemTabs[image.category];
                      const category = acc[galleryItemTab];
                      if (!category) {
                        acc[galleryItemTab] = [];
                      }
                      acc[ImagingCategoryEnumToGalleryItemTabs[image.category]].push(image);
                    }
                    return acc;
                  },
                  {
                    [GalleryItemTabs.FullSeries]: fullSeries,
                  } as CollaborationCategorizedPatientImages,
                );

                this.patchState((state) => {
                  return {
                    ...state,
                    galleryItems: {
                      data: tabImages,
                      state: DataState.Fetched,
                    },
                  };
                });
              },
              (error: HttpErrorResponse) => {
                this.toastService.error(this.translateService.instant('error_message_server_error_images'));
                console.error(error);
              },
            ),
          );
      }),
    ),
  );

  public mapImagesToAttachment = this.effect(($: Observable<void>) =>
    $.pipe(
      concatLatestFrom(() => [
        this.select((state) => state.siteId),
        this.select((state) => state.patientId),
        this.select((state) => state.selectedImages),
      ]),
      switchMap(([_, siteId, patientId, images]) => {
        this.setMappedMessagesAreLoading();
        return forkJoin(
          images.map((image) => {
            if (image.file) {
              return of(image);
            }
            return this.dataService
              .getImage(siteId, patientId, image.id, 'response', undefined, {
                context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
              })
              .pipe(
                take(1),
                map((response) => {
                  const contentType = response.headers.get('content-type');
                  return {
                    ...image,
                    mediaType: MessageContentAttachmentMediaTypeEnum.IMAGE,
                    category: image.category,
                    file: new File([response.body!], image.toothPositionString ?? '', { type: contentType ?? response.body?.type }),
                    url: URL.createObjectURL(response.body!),
                  } as MessageAttachment;
                }),
                catchError((error) => {
                  this.setMappedMessagesAreErrored();
                  this.toastService.error(this.translateService.instant('error_message_server_error_image'));
                  console.error(error);
                  return EMPTY;
                }),
              );
          }),
        ).pipe(
          tapResponse(
            (mappedImages) => {
              this.patchState((state) => {
                const imagesWithFile = images.filter((image) => image.file);
                return {
                  ...state,
                  attachments: {
                    data: [...mappedImages, ...imagesWithFile],
                    state: DataState.Fetched,
                  },
                };
              });
            },
            (error) => {
              this.setMappedMessagesAreErrored();
              console.error(error);
            },
          ),
        );
      }),
    ),
  );

  protected override mapStateToViewModel(state: CollaborationImageSelectorDialogState): CollaborationImageSelectorDialogViewModel {
    return {
      activeTab: state.activeTab,
      galleryItems: state.galleryItems.data,
      selectedImages: state.selectedImages,
      attachments: state.attachments,
    };
  }
}
